import {
  differenceInYears,
  isAfter,
  isBefore,
  isValid,
  lastDayOfMonth,
  parse,
} from 'date-fns';
import * as yup from 'yup';

import { DATE_FORMATS } from '../../constants';
import type {
  AnyDocument,
  WorkflowDataResolverDependencies,
  WorkflowStepFormFieldBlock,
  WorkflowStepFormFieldCustomValidationRule,
  WorkflowStepFormFieldValidationRules,
} from '../../types';
import { resolveEvaluatable } from '../evaluatable';
import {
  evaluate,
  notNullOrUndefinedOrEmpty,
  stringToNumber,
  yupSchemaIsArray,
  yupSchemaIsMixed,
  yupSchemaIsString,
} from '../general';
import {
  getPhoneValidationRegex,
  PERSON_NAME_REGEX,
  PERSON_NAME_VALIDATION_MESSAGE,
  PhoneType,
  ValidationRequiredWhenFn,
} from '../validation';
import { customRule } from './customRule';
import { getFlattenedFormFieldBlocks } from './getFlattenedFormFieldBlocks';
import { getYupSchemaForFormFieldBlock } from './getYupSchemaForFormFieldBlock';

type WorkflowStepFormFieldBlockWithValidationRules = Omit<
  WorkflowStepFormFieldBlock,
  'validationRules'
> & {
  validationRules: WorkflowStepFormFieldValidationRules;
};

const getExceptionMessageForYupRequiredRule = (
  block: WorkflowStepFormFieldBlockWithValidationRules,
): string | undefined => {
  if (block.componentType === 'date-picker') {
    return `This is a required field and must match the following format: DD/MM/YYYY`;
  }

  return undefined;
};

const getValidationSchemaFromFormFieldBlock = (
  block: WorkflowStepFormFieldBlockWithValidationRules,
  rootDocument: AnyDocument | undefined,
) =>
  Object.keys(block.validationRules).reduce((schema, key) => {
    const ruleName = key as keyof WorkflowStepFormFieldValidationRules;
    const value = block.validationRules?.[ruleName];
    const valueAsRecord = value as Record<string, ValidationRequiredWhenFn>;
    const valueAsNumber = value as number;
    const valueAsTuple = value as [string, string];
    const valueAsComparisonParams = value as [string, string, string, string];

    const isValidDate = (dateString: any | undefined, format: string) => {
      const parsedDate = parse(dateString, format, new Date());
      return isValid(parsedDate);
    };

    if (ruleName === 'customRule') {
      return customRule(
        value as WorkflowStepFormFieldCustomValidationRule,
        schema,
        rootDocument,
      );
    }

    if (yupSchemaIsString(schema)) {
      switch (ruleName) {
        case 'date': {
          const validationMessage =
            'This field must match the following date format: DD/MM/YYYY';

          if (block.componentType === 'date-picker') {
            if (value) {
              return schema.test(
                'is-valid-date',
                `${validationMessage} and be a valid date`,
                (valueToValidate) => {
                  if (!valueToValidate) return false;
                  return isValidDate(valueToValidate, DATE_FORMATS.keys);
                },
              );
            }
          }

          if (block.componentType === 'number-input') {
            return schema.matches(/^\d{2}\/\d{2}\/\d{4}$/, validationMessage);
          }

          return schema.matches(/^\d{4}-\d{2}-\d{2}$/, validationMessage);
        }
        case 'monthFullYearDate': {
          const validationMessage =
            'This field must match the following date format: MM/YYYY';
          return schema.matches(/^\d{2}\/\d{4}$/, validationMessage);
        }

        case 'nullable':
          return schema.nullable();

        case 'email':
          return schema.email();

        case 'is':
          return schema.oneOf(
            [value],
            `This field must match the value '${value}'`,
          );

        case 'length':
          return schema.length(valueAsNumber);

        case 'maxCharLength':
          return schema.max(valueAsNumber);

        case 'minNumericValue': {
          return schema.test(
            'minNumericValue',
            `This field must be greater than or equal to ${valueAsNumber}`,
            (valueToValidate) =>
              valueToValidate
                ? stringToNumber(valueToValidate as string) >= valueAsNumber
                : true,
          );
        }

        case 'maxNumericValue': {
          return schema.test(
            'maxNumericValue',
            `This field must be less than or equal to ${valueAsNumber}`,
            (valueToValidate) =>
              valueToValidate
                ? stringToNumber(valueToValidate as string) <= valueAsNumber
                : true,
          );
        }

        case 'onlyLetters': {
          return schema.matches(
            /^[aA-zZ\s]*$/,
            'Only alphabets are allowed for this field',
          );
        }

        case 'onlyCharactersAndPunctuation': {
          return schema.matches(
            /^[a-zA-Z,\-'']+$/,
            'Only characters and punctuation are allowed for this field',
          );
        }

        case 'personName': {
          return schema.matches(
            PERSON_NAME_REGEX,
            PERSON_NAME_VALIDATION_MESSAGE,
          );
        }

        case 'minAge': {
          const minAgeMessage =
            block.validationRules?.minAgeMessage ||
            `You must be at least ${valueAsNumber} years old`;
          return schema.test(
            'DOB',
            minAgeMessage,
            (valueToValidate) =>
              differenceInYears(new Date(), new Date(valueToValidate || '')) >=
              valueAsNumber,
          );
        }

        case 'onlyNumbers': {
          return schema.matches(/^\d*$/, 'The field should have digits only');
        }

        case 'min':
          return schema.min(valueAsNumber);

        case 'phone':
          return schema.test(
            'phone',
            'This field must be a valid phone number',
            (valueToValidate) => {
              if (!valueToValidate) return true;
              return getPhoneValidationRegex(PhoneType.Any).test(
                valueToValidate,
              );
            },
          );

        case 'phoneMobile':
          return schema.test(
            'phoneMobile',
            'This field must be a valid mobile number',
            (valueToValidate) => {
              if (!valueToValidate) return true;
              return getPhoneValidationRegex(PhoneType.Mobile).test(
                valueToValidate,
              );
            },
          );

        case 'phoneLandline':
          return schema.test(
            'phoneLandline',
            'This field must be a valid landline number',
            (valueToValidate) => {
              if (!valueToValidate) return true;
              return getPhoneValidationRegex(PhoneType.Landline).test(
                valueToValidate,
              );
            },
          );

        case 'lessThanNumericField': {
          return schema.when([valueAsTuple[0]], ([fieldValue], fieldSchema) =>
            fieldSchema.test(
              'lessThanNumericField',
              `This field must be less than ${valueAsTuple[1]}.`,
              (valueToValidate: string | undefined) =>
                valueToValidate
                  ? stringToNumber(valueToValidate) < stringToNumber(fieldValue)
                  : true,
            ),
          );
        }

        case 'lessThanOrEqualNumericField': {
          return schema.when([valueAsTuple[0]], ([fieldValue], fieldSchema) =>
            fieldSchema.test(
              'lessThanOrEqualNumericField',
              `This field must be less than or equal ${valueAsTuple[1]}.`,
              (valueToValidate: string | undefined) =>
                valueToValidate && fieldValue
                  ? stringToNumber(valueToValidate) <=
                    stringToNumber(fieldValue)
                  : true,
            ),
          );
        }

        case 'withinPercentageRangeOfField': {
          return schema.when(
            [valueAsComparisonParams[0]],
            ([fieldValue], fieldSchema) => {
              // The referenced field has no value yet (e.g. the base rate has not
              // been calculated), so there is no range to validate against
              if (!notNullOrUndefinedOrEmpty(fieldValue)) return fieldSchema;

              const baseValue = stringToNumber(fieldValue);
              const minOffset = stringToNumber(valueAsComparisonParams[2]); // Minimum offset (e.g., `-2`)
              const maxOffset = stringToNumber(valueAsComparisonParams[3]); // Maximum offset (e.g., `0`)

              const minValue = parseFloat((baseValue + minOffset).toFixed(2));
              const maxValue = parseFloat((baseValue + maxOffset).toFixed(2));

              return fieldSchema.test(
                'withinPercentageRangeOfField',
                `This field must be between ${minValue.toFixed(2)}% and ${maxValue.toFixed(2)}%.`,
                (valueToValidate: string | undefined) => {
                  if (!valueToValidate) return true; // Skip validation if the value is missing

                  const numericValue = stringToNumber(valueToValidate);

                  return numericValue >= minValue && numericValue <= maxValue;
                },
              );
            },
          );
        }

        case 'required': {
          let clonedSchema = schema.required(
            getExceptionMessageForYupRequiredRule(block),
          );

          // @IMPROVEMENT Slightly gronk
          if (block.componentType === 'consent') {
            clonedSchema = clonedSchema.oneOf(['true']);
          } else {
            clonedSchema = clonedSchema.trim();
          }

          return clonedSchema;
        }

        // If any condition is met, we return the required result: using the OR operator.
        case 'requiredWhen': {
          let clonedSchema = schema.clone();

          Object.entries(valueAsRecord).forEach(([valueKey, isRequiredFn]) => {
            clonedSchema = clonedSchema.when(valueKey, (keyBy, fieldSchema) => {
              // because yup wraps single-key when() values in an array: keyBy = [fieldValue]
              const fieldValue = Array.isArray(keyBy) ? keyBy[0] : keyBy;
              return isRequiredFn(fieldValue)
                ? fieldSchema.trim().required()
                : fieldSchema;
            });
          });

          return clonedSchema;
        }

        // If all condition are met, we return the required result: using the AND operator.
        case 'requiredWhenAll': {
          let clonedSchema = schema.clone();

          clonedSchema = clonedSchema.test(
            'required-when-all',
            'This is a required field',
            function (testValue) {
              const { parent } = this;

              const allConditionsMet = Object.entries(valueAsRecord).every(
                ([valueKey, isRequiredFn]) => isRequiredFn(parent?.[valueKey]),
              );

              return !!testValue || !allConditionsMet;
            },
          );

          return clonedSchema;
        }

        case 'notEqualTo': {
          return schema.notOneOf(
            [yup.ref(valueAsTuple[0]), null],
            `The value cannot be equal to ${valueAsTuple[1]}`,
          );
        }

        case 'beforeDate': {
          return schema.test(
            'beforeDate',
            `Date must be before ${valueAsTuple[1]}.`,
            function (valueDate) {
              if (!valueDate) return true;
              const { parent } = this;
              const comparison = new Date(
                parent[valueAsTuple[0]] || valueAsTuple[0],
              );

              return isBefore(new Date(valueDate), comparison);
            },
          );
        }

        case 'afterDate': {
          return schema.test(
            'afterDate',
            `Date must be after ${valueAsTuple[1]}.`,
            function (valueDate) {
              if (!valueDate) return true;
              const { parent } = this;
              const comparison = new Date(
                parent[valueAsTuple[0]] || valueAsTuple[0],
              );

              return isAfter(new Date(valueDate), comparison);
            },
          );
        }

        case 'notInPastDate': {
          const { day, monthYear } = value as {
            day?: string;
            monthYear: string;
          };

          return schema.test(
            'notInPastDate',
            'Expiry date must be in the future',
            function (_val) {
              const { parent } = this;
              const today = new Date();

              const monthYearValue = parent[monthYear];
              if (!monthYearValue) return true;

              const [monthStr, yearStr] = monthYearValue.split('/');
              if (!monthStr || !yearStr) return true;

              const year = parseInt(yearStr, 10);
              const month = parseInt(monthStr, 10) - 1;

              if (day) {
                const dayValue = parent[day];
                if (dayValue) {
                  const dayStr = String(dayValue).padStart(2, '0');
                  const fullDateStr = `${dayStr}/${monthStr}/${yearStr}`;
                  const parsed = parse(
                    fullDateStr,
                    DATE_FORMATS.standard,
                    new Date(),
                  );

                  return isValid(parsed) && !isBefore(parsed, today);
                }
              }

              const endOfMonth = lastDayOfMonth(new Date(year, month));
              return !isBefore(endOfMonth, today);
            },
          );
        }
        default:
      }
    } else if (yupSchemaIsMixed(schema)) {
      if (ruleName === 'required') {
        return schema.required(getExceptionMessageForYupRequiredRule(block));
      }
    } else if (yupSchemaIsArray(schema)) {
      const arraySchema = schema as yup.ArraySchema<
        string[] | undefined,
        object
      >;
      switch (ruleName) {
        case 'required':
          return arraySchema.required().min(1, 'This is a required field');

        case 'requiredWhen': {
          let clonedSchema = arraySchema.clone();

          Object.entries(valueAsRecord).forEach(([valueKey, isRequiredFn]) => {
            clonedSchema = clonedSchema.when(valueKey, (keyBy, fieldSchema) => {
              // Yup v1 wraps single-key when() values in an array: keyBy = [fieldValue]
              const fieldValue = Array.isArray(keyBy) ? keyBy[0] : keyBy;
              return isRequiredFn(fieldValue)
                ? fieldSchema.required().min(1, 'This is a required field')
                : fieldSchema;
            });
          });

          return clonedSchema;
        }

        case 'requiredWhenAll': {
          let clonedSchema = arraySchema.clone();

          clonedSchema = clonedSchema.test(
            'required-when-all',
            'This is a required field',
            function (testValue) {
              const { parent } = this;

              const allConditionsMet = Object.entries(valueAsRecord).every(
                ([valueKey, isRequiredFn]) => isRequiredFn(parent?.[valueKey]),
              );

              return (
                !allConditionsMet ||
                (Array.isArray(testValue) && testValue.length > 0)
              );
            },
          );

          return clonedSchema;
        }

        default:
      }
    }

    return schema;
  }, getYupSchemaForFormFieldBlock(block));

export const getValidationSchemaForForm = (
  formFieldBlocks: WorkflowStepFormFieldBlock[],
  rootDocument: AnyDocument | undefined,
  getFormValue: (key: string) => unknown,
): yup.AnyObjectSchema => {
  const workflowDataResolverDependencies: WorkflowDataResolverDependencies = {
    rootDocument,
    getFormValue,
  };

  const flattenedFormFieldBlocks = getFlattenedFormFieldBlocks(formFieldBlocks);

  const isBlockDisplayed = (block: WorkflowStepFormFieldBlock): boolean => {
    if (block.displayWhen) {
      const resolvedEvaluatable = resolveEvaluatable(
        block.displayWhen,
        workflowDataResolverDependencies,
      );
      return Boolean(evaluate(resolvedEvaluatable));
    }
    return true;
  };

  const validFormFieldBlocks =
    flattenedFormFieldBlocks.filter(isBlockDisplayed);

  const hiddenFormFieldBlocksToNull = flattenedFormFieldBlocks.filter(
    (block) => block.shouldNullValueWhenHidden && !isBlockDisplayed(block),
  );

  return yup.object({
    ...validFormFieldBlocks.reduce(
      (schema, formFieldBlock) => ({
        ...schema,
        ...(formFieldBlock.validationRules && {
          [formFieldBlock.name]: getValidationSchemaFromFormFieldBlock(
            formFieldBlock as WorkflowStepFormFieldBlockWithValidationRules,
            rootDocument,
          ),
        }),
      }),
      {},
    ),
    ...hiddenFormFieldBlocksToNull.reduce(
      (schema, formFieldBlock) => ({
        ...schema,
        [formFieldBlock.name]: yup
          .mixed()
          .nullable()
          .transform(() => null),
      }),
      {},
    ),
  });
};
